/**
 * ============================================================================
 * TransactionsStats - KPI Cards Component
 * ============================================================================
 */

import React from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Wallet, Zap } from 'lucide-react';
import { formatCurrency } from '../../utils/transactionFormatters';
import type { TransactionsStats } from '../../types/transactions';

interface TransactionsStatsProps extends TransactionsStats {
  isSplitVenue?: boolean;
}

export function TransactionsStats({
  grossRevenue,
  totalPayouts,
  totalRefunds,
  currentBalance,
  isSplitVenue = false,
}: TransactionsStatsProps) {
  const stats = isSplitVenue
    ? [
        {
          label: 'Gross Revenue',
          value: formatCurrency(grossRevenue),
          subtext: 'All bookings collected',
          icon: TrendingUp,
          colorClass: 'bg-green-50 text-green-600',
        },
        {
          label: 'Total Refunds',
          value: formatCurrency(totalRefunds),
          subtext: 'Customer refunds',
          icon: RefreshCw,
          colorClass: 'bg-purple-50 text-purple-600',
        },
        {
          label: 'Auto-Settled',
          value: 'Via Paystack',
          subtext: 'Direct bank settlement T+1',
          icon: Zap,
          colorClass: 'bg-blue-50 text-blue-600',
        },
      ]
    : [
        {
          label: 'Gross Revenue',
          value: formatCurrency(grossRevenue),
          subtext: 'All transactions',
          icon: TrendingUp,
          colorClass: 'bg-green-50 text-green-600',
        },
        {
          label: 'Total Payouts',
          value: formatCurrency(totalPayouts),
          subtext: 'Money transferred',
          icon: TrendingDown,
          colorClass: 'bg-orange-50 text-orange-600',
        },
        {
          label: 'Total Refunds',
          value: formatCurrency(totalRefunds),
          subtext: 'Customer refunds',
          icon: RefreshCw,
          colorClass: 'bg-purple-50 text-purple-600',
        },
        {
          label: 'Current Balance',
          value: formatCurrency(currentBalance),
          subtext: 'Available funds',
          icon: Wallet,
          colorClass: 'bg-blue-50 text-blue-600',
        },
      ];

  return (
    <div className={`grid gap-3 sm:gap-4 ${isSplitVenue ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'}`}>
      {stats.map((stat, idx) => (
        <div
          key={idx}
          className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-gray-500 uppercase mb-1 truncate">
                {stat.label}
              </p>
              <p className="text-lg sm:text-xl font-black text-gray-900 truncate">
                {stat.value}
              </p>
              <p className="text-xs text-gray-500 truncate">{stat.subtext}</p>
            </div>
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-2 ${stat.colorClass}`}
            >
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
